
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Edit3 } from "lucide-react";

export default function EditCustomerModal({
  isOpen, onClose, customer, onSubmit }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    shipping_address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    billing_address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useSameAddress, setUseSameAddress] = useState(true);

  useEffect(() => {
    if (customer) {
      setFormData({
        full_name: customer.full_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        shipping_address: customer.shipping_address || {
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country: ''
        },
        billing_address: customer.billing_address || {
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country: ''
        },
        notes: customer.notes || ''
      });

      // Check if addresses are the same
      const shipping = customer.shipping_address || {};
      const billing = customer.billing_address || {};
      const addressesMatch = JSON.stringify(shipping) === JSON.stringify(billing);
      setUseSameAddress(addressesMatch);
    }
  }, [customer]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));

    // If using same address, sync billing with shipping
    if (useSameAddress && addressType === 'shipping_address') {
      setFormData(prev => ({
        ...prev,
        billing_address: {
          ...prev.billing_address,
          [field]: value
        }
      }));
    }
  };

  const handleSameAddressToggle = (checked) => {
    setUseSameAddress(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        billing_address: { ...prev.shipping_address }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(customer.id, formData);
    } catch (error) {
      console.error('Error updating customer:', error);
    }
    
    setIsSubmitting(false);
  };

  if (!customer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Edit3 className="w-5 h-5" />
            Edit Customer
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">{t('customers.create.basic_info')}</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  placeholder={t('placeholders.auth.full_name')}
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('placeholders.auth.email')}
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">{t('customers.create.phone')}</Label>
              <Input
                id="phone"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>
          </div>

          {/* Shipping Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900">{t('customers.create.shipping_address')}</h3>
            
            <div>
              <Label htmlFor="shipping_street">{t('customers.create.street')}</Label>
              <Input
                id="shipping_street"
                placeholder={t('placeholders.auth.address')}
                value={formData.shipping_address?.street || ''}
                onChange={(e) => handleAddressChange('shipping_address', 'street', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shipping_city">{t('customers.create.city')}</Label>
                <Input
                  id="shipping_city"
                  placeholder={t('placeholders.auth.city')}
                  value={formData.shipping_address?.city || ''}
                  onChange={(e) => handleAddressChange('shipping_address', 'city', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="shipping_state">{t('customers.create.state')}</Label>
                <Input
                  id="shipping_state"
                  placeholder={t('placeholders.auth.state')}
                  value={formData.shipping_address?.state || ''}
                  onChange={(e) => handleAddressChange('shipping_address', 'state', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shipping_postal">{t('customers.create.postal_code')}</Label>
                <Input
                  id="shipping_postal"
                  placeholder={t('placeholders.auth.postal_code')}
                  value={formData.shipping_address?.postal_code || ''}
                  onChange={(e) => handleAddressChange('shipping_address', 'postal_code', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="shipping_country">{t('customers.create.country')}</Label>
                <Input
                  id="shipping_country"
                  placeholder={t('placeholders.auth.country')}
                  value={formData.shipping_address?.country || ''}
                  onChange={(e) => handleAddressChange('shipping_address', 'country', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">{t('customers.create.billing_address')}</h3>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={useSameAddress}
                  onChange={(e) => handleSameAddressToggle(e.target.checked)}
                  className="rounded"
                />
                Same as shipping address
              </label>
            </div>

            {!useSameAddress && (
              <>
                <div>
                  <Label htmlFor="billing_street">{t('customers.create.street')}</Label>
                  <Input
                    id="billing_street"
                    placeholder={t('placeholders.auth.address')}
                    value={formData.billing_address?.street || ''}
                    onChange={(e) => handleAddressChange('billing_address', 'street', e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="billing_city">{t('customers.create.city')}</Label>
                    <Input
                      id="billing_city"
                      placeholder={t('placeholders.auth.city')}
                      value={formData.billing_address?.city || ''}
                      onChange={(e) => handleAddressChange('billing_address', 'city', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="billing_state">{t('customers.create.state')}</Label>
                    <Input
                      id="billing_state"
                      placeholder={t('placeholders.auth.state')}
                      value={formData.billing_address?.state || ''}
                      onChange={(e) => handleAddressChange('billing_address', 'state', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="billing_postal">{t('customers.create.postal_code')}</Label>
                    <Input
                      id="billing_postal"
                      placeholder={t('placeholders.auth.postal_code')}
                      value={formData.billing_address?.postal_code || ''}
                      onChange={(e) => handleAddressChange('billing_address', 'postal_code', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="billing_country">{t('customers.create.country')}</Label>
                    <Input
                      id="billing_country"
                      placeholder={t('placeholders.auth.country')}
                      value={formData.billing_address?.country || ''}
                      onChange={(e) => handleAddressChange('billing_address', 'country', e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">{t('customers.create.notes')}</Label>
            <Textarea
              id="notes"
              placeholder={t('placeholders.auth.notes')}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="h-24"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !formData.full_name || !formData.email}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting ? 'Updating...' : 'Update Customer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
